import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useFPContext } from '../context/FuturePathContext';

const LABELS = {
  state: 'State', city: 'City', zip_code: 'ZIP', age_band: 'Age',
  education_stage: 'Education', employment_status: 'Work', occupation: 'Doing now',
  target_career: 'Target', income_floor: 'Income floor', interest_code: 'Interest code',
  personality_type: 'Type', primary_goal: 'Goal', join_reason: 'Why you came',
  geographic_flexibility: 'Would move', grade_level: 'Grade', skills_count: 'Skills on file',
};

const SOURCE_WORDS = {
  user: 'you told us', account: 'your account', derived: 'calculated',
  inferred: 'inferred', patricia: 'from Patricia', external: 'verified source',
};

const money = (v) => (typeof v === 'number' ? `$${Math.round(v).toLocaleString()}` : v);

/**
 * "Never ask twice." Every page that could re-ask something shows what is already on file,
 * where it came from, and links to change it, instead of putting the question up again.
 */
export const KnownFactsStrip = ({ keys, className = '' }) => {
  const { allKnown } = useFPContext();
  const [open, setOpen] = useState(false);
  const show = (keys || Object.keys(LABELS)).filter(k => allKnown?.[k] !== undefined
    && allKnown[k].value !== null && allKnown[k].value !== '' && allKnown[k].value !== 0);
  if (show.length === 0) return null;
  const visible = open ? show : show.slice(0, 6);

  return (
    <div className={`rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] px-4 py-3 ${className}`}
      data-testid="known-facts-strip">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />
        <span className="text-[10px] uppercase tracking-[0.14em] text-emerald-200/80 shrink-0">
          Already on file, we won't ask again
        </span>
        {visible.map(k => (
          <span key={k} className="inline-flex items-baseline gap-1.5 text-xs" data-testid={`known-${k}`}>
            <span className="text-slate-300">{LABELS[k] || k}</span>
            <span className="text-white font-medium">
              {k === 'income_floor' ? money(allKnown[k].value) : String(allKnown[k].value)}
            </span>
            <span className="text-[10px] text-slate-300">{SOURCE_WORDS[allKnown[k].source] || allKnown[k].source}</span>
          </span>
        ))}
        {show.length > 6 && (
          <button onClick={() => setOpen(v => !v)} data-testid="known-facts-toggle"
            className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-100">
            {open ? <>fewer <ChevronUp className="w-3 h-3" /></> : <>+{show.length - 6} more <ChevronDown className="w-3 h-3" /></>}
          </button>
        )}
        <Link to="/app/profile" data-testid="known-facts-edit"
          className="ml-auto text-xs text-slate-300 hover:text-white">Something changed?</Link>
      </div>
    </div>
  );
};

export default KnownFactsStrip;
